import axios from 'axios'
import hash from 'object-hash'

import { commonChartOptions, Palette } from '../'
import { CompassAPIDataBase, getDataSources } from '../utils/compass'
import { capitalizeString } from '../utils/string'
import type { BaseContext } from './'

export namespace FinancialFraud {
  export namespace BenfordLaw {
    type BenfordLawChartId = 'benford_law'

    const CONTEXT_ID = 'equity:financial_fraud:benford_law'
    const FINANCIAL_FRAUD_CHARTS_PATH = '/api/charts/financial-fraud'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      context: typeof CONTEXT_ID
      chart: BenfordLawChartId
      title: string
      ticker: string
    }

    interface CompassAPICategoryDataPoint {
      category: string
      value: number
    }

    interface CompassAPICategoryData {
      data: CompassAPICategoryDataPoint[]
      name: string
    }

    interface CompassAPICategoryChartData extends CompassAPIDataBase {
      data: CompassAPICategoryData[]
      title: string
    }

    export type ChartData = CompassAPICategoryChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: FINANCIAL_FRAUD_CHARTS_PATH + '/benford-law/benford-law',
        method: 'GET',
        params: { ticker: config.ticker },
      }).then((response) => response.data)

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data.map((s, idx) => ({
        data: s.data.map(({ value }) => value),
        dataLabels: {
          enabled: s.name === 'empirical',
          format: '{y:.2f} %',
        },
        id: `series-${idx}`,
        marker: { enabled: false },
        name: capitalizeString(s.name),
        type: s.name === 'empirical' ? 'column' : 'line',
        yAxis: 'default',
        zIndex: s.name === 'empirical' ? 10 : 100,
        ...(s.name !== 'empirical'
          ? {
              lineWidth: 0,
              marker: { enabled: true, radius: 4 },
              states: { hover: { lineWidthPlus: 0 } },
            }
          : {}),
      }))

      const tooltip = {
        shared: true,
        split: false,
        valueDecimals: 2,
        valueSuffix: '%',
      }

      const yAxis = [
        {
          id: 'default',
          labels: { format: '{value}%' },
          title: { text: '' },
        },
      ]

      const xAxis = [
        {
          type: 'category',
          categories: data.data[0].data.map(({ category }) => category),
        },
      ]

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: {
          ...commonChartOptions,
          ...getDataSources(data.data_sources),
          plotOptions: { column: { minPointLength: 1 } },
          series: [
            ...series,
            {
              ...series.filter((s) => s.name === 'Empirical')[0],
              color: Palette.darkGreen,
              enableMouseTracking: false,
              id: 'empirical-line',
              showInLegend: false,
              type: 'line',
            },
          ],
          tooltip,
          xAxis,
          yAxis,
        },
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }
}
